import { API_BASE_URL, fetchWithAuth, getDefaultHeaders } from '../config/api';
import type { Opportunity } from './opportunitiesApi';

/**
 * Business enquiries — both ends of one feature, which is why they share a file.
 *
 * - {@link submitBusinessEnquiry} is what a STRANGER calls from a public marketplace profile. It is
 *   anonymous and must stay that way (see its own comment).
 * - {@link enquiriesApi} is what the OWNER's inbox calls. Ordinary authenticated, business-scoped
 *   requests through `fetchWithAuth`, exactly like every other in-app service.
 *
 * Every shape below mirrors a server type. Nothing here derives, re-checks or reformats a value the
 * server sent — see `marketplaceApi`'s credibility type for the same rule stated where it bites
 * hardest.
 */

const isProduction = import.meta.env.PROD;

/**
 * The absolute URL of the anonymous submit endpoint.
 *
 * Built the same way `marketplaceApi.marketplaceEndpoint` builds the public marketplace URLs, and
 * deliberately NOT with `config/api`'s `createEndpoint`: that path is for authenticated in-app
 * calls, and this one is reached from the bare public profile page.
 */
function publicEnquiryEndpoint(): string {
  if (isProduction) {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/api/public/business-enquiries`;
  }

  const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;
  if (apiServerUrl) {
    const base = apiServerUrl.replace(/\/+$/, '');
    const withApi = base.includes('/api') ? base : `${base}/api`;
    return `${withApi}/public/business-enquiries`;
  }

  return 'http://localhost:5183/api/public/business-enquiries';
}

/**
 * What `POST api/public/business-enquiries` accepts. Mirrors
 * `PublicBusinessEnquiryController.BusinessEnquiryRequest`.
 *
 * `businessId` names the business; it is NOT a destination. The server resolves the recipient from
 * that business's own record, so nothing a caller puts in this body can steer where mail goes.
 */
export interface BusinessEnquiryRequest {
  businessId: string;
  name: string;
  /** The ENQUIRER's address, for the business to reply to. */
  email: string;
  phone?: string;
  suburb?: string;
  message: string;
  /**
   * The honeypot. Hidden from people by the form and required by the server to be empty — a filled
   * one is answered with the same 202 a real submission gets and stored nowhere. Always send it
   * (empty), because a field the form never posts is one a bot can notice is absent.
   *
   * **The name is deliberately meaningless and must stay that way.** It was `website`, which a
   * browser or password manager fills from a saved business profile — `autocomplete="off"` does not
   * reliably stop that — silently discarding a real customer's enquiry while showing them a
   * success. Never name it after anything an autofill heuristic knows.
   */
  contactPreferenceCode?: string;
}

/**
 * Sends an enquiry. Resolves on the server's bare 202; throws with the server's own words when it
 * refused, so the form can say what was actually wrong rather than "something went wrong".
 *
 * A PLAIN `fetch`, on purpose:
 *
 * - No `fetchWithAuth`. The endpoint is `[AllowAnonymous]` and the page is public — a visitor has
 *   no session, and routing this through the authenticated helper would attach `X-Business-Id`
 *   from whatever business a logged-in viewer last selected, which has nothing to do with the
 *   business being enquired with.
 * - No `credentials: 'include'`. The endpoint's CORS policy is the AllowAnyOrigin "PublicStats"
 *   one, and a browser refuses a credentialed cross-origin request against a wildcard origin.
 *
 * The enquiry goes to this endpoint and nowhere else — the form's "shared only with this business"
 * line is a claim about this function, and there is nothing else here for it to be wrong about.
 */
export async function submitBusinessEnquiry(request: BusinessEnquiryRequest): Promise<void> {
  const response = await fetch(publicEnquiryEndpoint(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    // The rate limiter answers with no body, and "please try again" is the wrong advice for it:
    // someone at their hourly limit will retry straight away and fail again. Told to wait, they
    // come back later instead of concluding the form is broken.
    if (response.status === 429) {
      throw new Error(
        'You have sent a few enquiries recently — please try again a little later.',
      );
    }

    // The server's other refusals carry `{ error }` where it had something useful to say ("Please
    // tell the business a little more."). A 404 is bare, so it falls back to the generic line.
    const problem = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(problem?.error || 'Your enquiry could not be sent. Please try again.');
  }
}

/**
 * Mirrors `EnquiryStatus` in the domain — a lifecycle, not a set of flags.
 *
 * `won` and `lost` are historical: the inbox no longer writes them, but stored records carry them
 * and the public profile still counts `won` as a job won, so they remain part of the type.
 */
export type EnquiryStatus = 'new' | 'handled' | 'won' | 'lost' | 'archived';

/** Mirrors `BusinessEnquiriesController.ToDto`. No `ipAddress`, no `statusChangedByUserId`. */
export interface BusinessEnquiryDto {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  suburb: string | null;
  message: string;
  status: EnquiryStatus;
  statusChangedAt: string | null;
  /** False means the business was never told this arrived. The inbox must say so. */
  emailSent: boolean;
  submittedAt: string;
  /**
   * The Sales Pipeline deal made from this enquiry by "Add to pipeline", or null when none has
   * been. A pointer only — the deal may since have been deleted, which the deal route reports.
   */
  opportunityId?: string | null;
}

/** Mirrors the `BusinessEnquiriesController.AddToPipeline` response. */
export interface AddToPipelineResult {
  opportunityId: string;
  /** True when the enquiry was already on the board and no new deal was made. */
  alreadyExisted: boolean;
  opportunity: Opportunity;
}

export interface EnquiryStatsDto {
  /** Null below `minimumForRate` enquiries in the window — never a small-sample number. */
  responseRatePercent: number | null;
  /** Null at zero: a new member has not failed at anything. */
  jobsWon: number | null;
  rateWindowDays: number;
  minimumForRate: number;
}

const INBOX_URL = `${API_BASE_URL}/business-enquiries`;

/** The owner's inbox. Business-scoped server-side from the caller's own access, never from a body. */
export const enquiriesApi = {
  /** Newest first (the server's sort), optionally narrowed to one status. */
  list: (status?: EnquiryStatus): Promise<BusinessEnquiryDto[]> =>
    fetchWithAuth<BusinessEnquiryDto[]>(
      status ? `${INBOX_URL}?status=${encodeURIComponent(status)}` : INBOX_URL,
      { method: 'GET', headers: getDefaultHeaders() },
    ),

  /**
   * The two figures this business's public profile publishes, derived server-side by the SAME call
   * the public profile uses — so the dashboard can never quote a different rate from the one a
   * visitor sees.
   *
   * Both are null when they are not worth publishing. `responseRatePercent` measures enquiries the
   * business MARKED as acted on, not replies we observed, so nothing built on it may imply a reply
   * time.
   */
  stats: (): Promise<EnquiryStatsDto> =>
    fetchWithAuth<EnquiryStatsDto>(`${INBOX_URL}/stats`, {
      method: 'GET',
      headers: getDefaultHeaders(),
    }),

  /** How many are still `new`. Feeds the sidebar badge. */
  countNew: (): Promise<{ count: number }> =>
    fetchWithAuth<{ count: number }>(`${INBOX_URL}/count-new`, {
      method: 'GET',
      headers: getDefaultHeaders(),
    }),

  /** Moves one enquiry along. The server answers 204, so there is nothing to read back. */
  setStatus: (id: string, status: EnquiryStatus): Promise<void> =>
    fetchWithAuth<void>(`${INBOX_URL}/${encodeURIComponent(id)}/status`, {
      method: 'PUT',
      headers: getDefaultHeaders(),
      body: JSON.stringify({ status }),
    }),

  /**
   * Turns an enquiry into a Sales Pipeline deal. Idempotent server-side: an enquiry already on the
   * board answers with that deal (`alreadyExisted: true`) rather than making a second one.
   *
   * Only the note travels. The contact details on the deal come from the STORED enquiry, never from
   * this body. Does not change the enquiry's status — putting someone on the board is not replying.
   */
  addToPipeline: (id: string, note?: string): Promise<AddToPipelineResult> =>
    fetchWithAuth<AddToPipelineResult>(`${INBOX_URL}/${encodeURIComponent(id)}/deal`, {
      method: 'POST',
      headers: getDefaultHeaders(),
      body: JSON.stringify({ note }),
    }),
};
